/*
 * Shared load-more handler.
 *
 * Usage: <button class="load-more__btn" data-load-more
 *          data-next-url="{{ paginate.next.url }}"
 *          data-grid="#my-grid"
 *          data-item="#my-grid > .my-card"
 *          data-after="myCallback">
 *
 * - data-grid: CSS selector for the container in the current document.
 * - data-item: CSS selector applied to the *fetched* document to find items
 *              to append. Defaults to `${data-grid} > *`.
 * - data-after: optional name of a global function called after items are
 *               appended (receives the new nodes as an array).
 *
 * Uses event delegation so buttons inserted after AJAX filter/sort updates
 * work without re-binding.
 *
 * Back-button restore
 * -------------------
 * Infinite scroll is client-side only: appended pages live in the DOM of a
 * single page-load. Navigating to a PDP and pressing Back would otherwise drop
 * every loaded-more page (Shopify re-renders only page 1) and lose the scroll
 * position. We can't fix that server-side — {% paginate %} renders one page at
 * a time and its `by:` size caps at 50, so it can't re-render a large
 * cumulative set from a ?page=N URL.
 *
 * Instead we snapshot the grid HTML, the button's next-url, and the scroll
 * position into sessionStorage (keyed by the full URL incl. filters/sort), and
 * restore it before paint when we come back. No re-fetch, no flash, works for
 * every section using this handler.
 *
 * Mobile notes: iOS/Android lean heavily on the back/forward cache (bfcache),
 * where the whole page (DOM + scroll) is frozen and resumed WITHOUT re-running
 * this script — so we must NOT force `scrollRestoration = 'manual'` (that
 * breaks native bfcache scroll restore) and we handle the `pageshow` resume.
 * When bfcache misses (a real reload), mobile browsers don't reliably report
 * the nav as `back_forward`, so we trigger DOM restore off whether the snapshot
 * actually holds more products than the server rendered, not off nav type. We
 * also save on `visibilitychange`→hidden, which fires reliably on mobile before
 * `pagehide`.
 */
(function () {
  var STORE_TTL = 30 * 60 * 1000; // snapshots older than 30 min are ignored
  var registry = {}; // gridSel -> { afterName }

  function pageKey() {
    return 'lm:' + location.pathname + location.search;
  }

  function buttonFor(gridSel) {
    return document.querySelector('[data-load-more][data-grid="' + gridSel + '"]');
  }

  function readSnapshot() {
    var raw;
    try {
      raw = sessionStorage.getItem(pageKey());
    } catch (e) {
      return null;
    }
    if (!raw) return null;
    var data;
    try {
      data = JSON.parse(raw);
    } catch (e) {
      return null;
    }
    if (!data || !data.grids || (Date.now() - data.ts) > STORE_TTL) return null;
    return data;
  }

  // Record every load-more grid present at parse time so we know what to
  // snapshot (selectors are stable across AJAX filter/sort swaps).
  function registerGrids() {
    var buttons = document.querySelectorAll('[data-load-more]');
    Array.prototype.forEach.call(buttons, function (btn) {
      var sel = btn.getAttribute('data-grid');
      if (!sel) return;
      registry[sel] = { afterName: btn.getAttribute('data-after') };
    });
  }

  function saveSnapshot() {
    try {
      var grids = {};
      Object.keys(registry).forEach(function (sel) {
        var grid = document.querySelector(sel);
        if (!grid) return;
        var btn = buttonFor(sel);
        grids[sel] = {
          html: grid.innerHTML,
          count: grid.children.length,
          nextUrl: btn ? btn.getAttribute('data-next-url') : null
        };
      });
      if (!Object.keys(grids).length) return;
      var data = { ts: Date.now(), scrollY: window.scrollY, grids: grids };
      sessionStorage.setItem(pageKey(), JSON.stringify(data));
    } catch (e) {
      /* quota / privacy mode — degrade to no restore */
    }
  }

  function scrollToSaved(y) {
    if (typeof y !== 'number') return;
    // Apply now, next frame, and after load — images reflowing (and any native
    // restore) can otherwise nudge the final position.
    window.scrollTo(0, y);
    requestAnimationFrame(function () { window.scrollTo(0, y); });
    window.addEventListener('load', function () { window.scrollTo(0, y); }, { once: true });
  }

  // Re-inject loaded pages when the server rendered fewer products than we had.
  // Runs on a fresh (bfcache-miss) load; deferred script => DOM is parsed.
  function restoreSnapshot() {
    var data = readSnapshot();
    if (!data) return;

    var restoredAny = false;
    Object.keys(data.grids).forEach(function (sel) {
      var grid = document.querySelector(sel);
      if (!grid) return;
      var snap = data.grids[sel];

      // Only restore if we're missing products (i.e. server gave us page 1
      // while the snapshot holds more). This is what identifies a return
      // visit, without depending on flaky mobile nav-type detection.
      if (!(snap.count > grid.children.length)) return;

      grid.innerHTML = snap.html;

      var btn = buttonFor(sel);
      if (btn) {
        if (snap.nextUrl) {
          btn.setAttribute('data-next-url', snap.nextUrl);
          btn.classList.remove('loading');
        } else if (btn.parentElement) {
          btn.parentElement.remove(); // all pages had been loaded
        }
      }

      var afterName = registry[sel] && registry[sel].afterName;
      if (afterName && typeof window[afterName] === 'function') {
        try {
          window[afterName](Array.prototype.slice.call(grid.children));
        } catch (e) {}
      }
      restoredAny = true;
    });

    if (restoredAny) scrollToSaved(data.scrollY);
  }

  // This script is deferred, so the DOM is parsed by the time it runs.
  registerGrids();
  restoreSnapshot();

  // Save on both signals: visibilitychange→hidden fires reliably on mobile
  // (tab switch / navigation), pagehide covers desktop and bfcache entry.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') saveSnapshot();
  });
  window.addEventListener('pagehide', saveSnapshot);

  // bfcache resume: DOM is already intact, but native scroll restore can be
  // skipped on some mobile browsers — re-apply our saved position.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    var data = readSnapshot();
    if (data) scrollToSaved(data.scrollY);
  });

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-load-more]');
    if (!btn) return;
    if (btn.classList.contains('loading')) return;

    var nextUrl = btn.getAttribute('data-next-url');
    if (!nextUrl) return;

    var gridSel = btn.getAttribute('data-grid');
    var grid = gridSel ? document.querySelector(gridSel) : null;
    if (!grid) return;

    var itemSel = btn.getAttribute('data-item') || gridSel + ' > *';
    var afterName = btn.getAttribute('data-after');

    btn.classList.add('loading');

    fetch(nextUrl)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newItems = Array.prototype.slice.call(doc.querySelectorAll(itemSel));
        var newBtn = doc.querySelector('[data-load-more]');

        newItems.forEach(function (node) { grid.appendChild(node); });

        if (afterName && typeof window[afterName] === 'function') {
          try { window[afterName](newItems); } catch (err) {}
        }

        if (newBtn) {
          btn.setAttribute('data-next-url', newBtn.getAttribute('data-next-url'));
          btn.classList.remove('loading');
        } else if (btn.parentElement) {
          btn.parentElement.remove();
        }
      })
      .catch(function () {
        btn.classList.remove('loading');
      });
  });
})();
